import { motion, AnimatePresence } from 'framer-motion'
import { Brain, Lightbulb, Target, Zap, BookOpen, Trophy, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, useEffect } from 'react'

const Hero = () => {
  const [currentBackground, setCurrentBackground] = useState(0)
  
  // Images de fond avec thème éducation/orange/bleu
  const backgroundImages = [
    "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
    "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80",
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80",
    "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
  ]

  const floatingIcons = [
    { Icon: Brain, delay: 0, x: -100, y: -50 },
    { Icon: Lightbulb, delay: 0.2, x: 100, y: -80 },
    { Icon: Target, delay: 0.4, x: -80, y: 50 },
    { Icon: Zap, delay: 0.6, x: 120, y: 60 },
    { Icon: BookOpen, delay: 0.8, x: -120, y: 100 },
    { Icon: Trophy, delay: 1, x: 90, y: -120 }
  ]

  // Changement automatique des images toutes les 6 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBackground((prev) => (prev + 1) % backgroundImages.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  const nextBackground = () => {
    setCurrentBackground((prev) => (prev + 1) % backgroundImages.length)
  }

  const prevBackground = () => {
    setCurrentBackground((prev) => (prev - 1 + backgroundImages.length) % backgroundImages.length)
  }

  // Variants pour les animations de transition
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 1.1,
      filter: "blur(10px) brightness(0.7)"
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      filter: "blur(0px) brightness(0.6)"
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.9,
      filter: "blur(10px) brightness(0.7)"
    })
  }

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-900/30 via-red-900/20 to-blue-900/30 pt-16">
      
      {/* Carrousel d'images de fond avec effets de filtre améliorés */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <AnimatePresence mode="wait" custom={1}>
          <motion.div
            key={currentBackground}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${backgroundImages[currentBackground]})`,
            }}
            custom={1}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.8 },
              scale: { duration: 0.8 },
              filter: { duration: 0.8 }
            }}
          />
        </AnimatePresence>
        
        {/* Overlays de couleur avec la nouvelle palette orange/rouge/bleu */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/25 via-red-600/20 to-blue-600/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-orange-900/40 via-transparent to-blue-900/40" />
        
        {/* Filtre supplémentaire pour assombrir et améliorer le contraste */}
        <div className="absolute inset-0 bg-black/40 backdrop-contrast-125 backdrop-brightness-75" />
        
        {/* Effet de grain subtil pour plus de texture */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/asfalt-dark.png')] opacity-15 mix-blend-overlay" />
      </div>

      {/* Contrôles du carrousel stylisés */}
      <div className="absolute top-1/2 left-6 transform -translate-y-1/2 z-20">
        <button
          onClick={prevBackground}
          className="p-3 rounded-full bg-white/15 backdrop-blur-md text-white hover:bg-orange-500/90 border border-white/30 hover:border-orange-300 transition-all duration-300 shadow-2xl shadow-orange-500/25 group"
        >
          <ChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </div>
      
      <div className="absolute top-1/2 right-6 transform -translate-y-1/2 z-20">
        <button
          onClick={nextBackground}
          className="p-3 rounded-full bg-white/15 backdrop-blur-md text-white hover:bg-blue-500/90 border border-white/30 hover:border-blue-300 transition-all duration-300 shadow-2xl shadow-blue-500/25 group"
        >
          <ChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </div>

      {/* Indicateurs de progression modernes */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
        {backgroundImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentBackground(index)}
            className={`w-3 h-3 rounded-full transition-all duration-500 transform ${
              index === currentBackground 
                ? 'bg-orange-400 scale-125 shadow-lg shadow-orange-400/60' 
                : 'bg-white/70 hover:bg-white hover:scale-110'
            }`}
          />
        ))}
      </div>

      {/* Icons flottantes avec la nouvelle palette */}
      <div className="absolute inset-0 overflow-hidden z-10">
        {floatingIcons.map(({ Icon, delay, x, y }, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{ left: `${50 + x/10}%`, top: `${50 + y/10}%` }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0.1, 0.5, 0.1],
              scale: [1, 1.3, 1],
              y: [0, -30, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ 
              duration: 6,
              delay,
              repeat: Infinity,
              repeatType: 'reverse'
            }}
          >
            <Icon className={`w-16 h-16 md:w-24 md:h-24 ${
              i % 3 === 0 
                ? 'text-orange-400/25' 
                : i % 3 === 1 
                  ? 'text-red-400/25' 
                  : 'text-blue-400/25'
            }`} />
          </motion.div>
        ))}
      </div>

      {/* Contenu principal */}
      <div className="container-custom relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Titre avec dégradé orange/rouge/bleu */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-gradient drop-shadow-2xl">
              GENIUS CLUB
            </h1>
            
            <p className="text-xl md:text-2xl text-white/95 mb-4 font-heading drop-shadow-2xl font-semibold tracking-wide">
              Centre Éducatif d'Excellence
            </p>
            
            <p className="text-lg md:text-xl text-white/90 mb-12 leading-relaxed drop-shadow-xl max-w-3xl mx-auto font-medium">
              Développez votre potentiel avec nos programmes innovants en mathématiques mentales, 
              coaching cognitif et neurolearning
            </p>
          </motion.div>

          {/* Boutons d'action */}
          <motion.div
            className="flex flex-col sm:flex-row gap-5 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <a 
              href="#services" 
              className="btn-primary text-lg px-10 py-4 font-semibold rounded-xl shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 transform hover:scale-105 border border-orange-400/40"
            >
              Découvrir nos services
            </a>
            
            <a 
              href="#game" 
              className="btn-secondary text-lg px-10 py-4 font-semibold rounded-xl shadow-2xl hover:shadow-blue-500/40 backdrop-blur-sm transition-all duration-300 transform hover:scale-105 border border-blue-400/40"
            >
              Essayer le jeu mental
            </a>
          </motion.div>

          {/* Grille de services */}
          <motion.div
            className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {[
              { icon: Brain, text: 'Mathématiques Mentales', color: 'orange' },
              { icon: Target, text: 'Performance & Concentration', color: 'red' },
              { icon: BookOpen, text: 'Coaching Scolaire', color: 'blue' },
              { icon: Zap, text: 'Neurolearning', color: 'orange' },
              { icon: Lightbulb, text: 'Cours de Soutien', color: 'red' },
              { icon: Trophy, text: 'Coaching Cognitif', color: 'blue' }
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`flex flex-col items-center gap-4 p-5 rounded-2xl backdrop-blur-lg border-2 transition-all duration-300 ${
                  item.color === 'orange' 
                    ? 'bg-orange-500/15 border-orange-400/40 hover:bg-orange-500/25 shadow-lg shadow-orange-500/20' 
                    : item.color === 'red'
                    ? 'bg-red-500/15 border-red-400/40 hover:bg-red-500/25 shadow-lg shadow-red-500/20'
                    : 'bg-blue-500/15 border-blue-400/40 hover:bg-blue-500/25 shadow-lg shadow-blue-500/20'
                }`}
                whileHover={{ 
                  scale: 1.05,
                  y: -5
                }}
                transition={{ duration: 0.3 }}
              >
                <item.icon className={`w-12 h-12 ${
                  item.color === 'orange' 
                    ? 'text-orange-300' 
                    : item.color === 'red'
                    ? 'text-red-300'
                    : 'text-blue-300'
                }`} />
                <span className="text-sm md:text-base font-semibold text-white text-center drop-shadow-lg">
                  {item.text}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Indicateur de défilement */}
      <motion.div
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20"
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-orange-300 rounded-full flex items-start justify-center p-2 backdrop-blur-sm bg-white/10">
          <motion.div
            className="w-1.5 h-1.5 bg-orange-300 rounded-full"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  )
}

export default Hero